<template>
  <div class="trk-panel mb-3">
    <div class="trk-panel-head trk-panel-head-split">
      <div>
        <h2><i class="fa fa-envelope-o"></i> Email OTP (verification codes)</h2>
        <p>When checkout asks an existing patient to verify their email — codes sent, failed, or verified.
          This is separate from card payment declines above.</p>
      </div>
    </div>

    <div class="trk-panel-body">
      <div class="pp-filters">
        <button v-for="k in types" :key="k.value" type="button" class="pp-kind"
                :class="{ active: otpType === k.value }" @click="setType(k.value)">
          {{ k.label }} <span class="pp-count">{{ typeCount(k.value) }}</span>
        </button>
        <select class="form-control form-control-sm pp-code" v-model="code" title="Failure reason">
          <option value="">All failure reasons</option>
          <option v-for="g in codeOptions" :key="g.code" :value="g.code">
            {{ reasonLabel(g) }} ({{ g.count }})
          </option>
        </select>
        <span v-if="loading" class="text-muted small"><b-spinner small /> Loading…</span>
      </div>

      <div v-if="data.truncated" class="text-muted small mb-2">
        Showing the latest 1,000 OTP events in this range — narrow the dates for full history.
      </div>

      <div class="pp-groups" v-if="visibleGroups.length">
        <button v-for="g in visibleGroups" :key="g.code" type="button" class="pp-group"
                :class="{ active: code === g.code }" @click="code = code === g.code ? '' : g.code">
          <span class="pp-group-count">{{ g.count }}</span>
          <span class="pp-group-text">
            <strong>{{ reasonLabel(g) }}</strong>
            <span class="text-muted small"><code>{{ g.code }}</code></span>
          </span>
        </button>
      </div>
    </div>

    <div class="trk-panel-body p-0">
      <div class="trk-table-scroll">
        <table class="table trk-table mb-0">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Reason</th>
              <th>Patient / email</th>
              <th>Journey</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in cases" :key="c.id">
              <td class="text-nowrap">{{ when(c.occurredAt) }}</td>
              <td>
                <span class="trk-pill" :class="eventPillClass(c.eventName)">{{ eventLabel(c.eventName) }}</span>
                <div class="text-muted small" v-if="c.step">Step: {{ c.step }}</div>
              </td>
              <td>
                <strong v-if="c.eventName === 'OTP_FAILED'">{{ reasonLabel(c) }}</strong>
                <span v-else class="text-muted">—</span>
                <div v-if="c.code"><code>{{ c.code }}</code></div>
                <div class="text-danger small" v-if="c.message" :title="c.message">{{ short(c.message) }}</div>
                <div class="text-muted small" v-if="c.apiPath">{{ c.apiPath }}<span v-if="c.httpStatus"> · HTTP {{ c.httpStatus }}</span></div>
              </td>
              <td>
                <router-link v-if="c.patientId" :to="'/patient/' + c.patientId">{{ c.email || ('Patient #' + c.patientId) }}</router-link>
                <span v-else-if="c.email">{{ c.email }}</span>
                <span v-else class="text-muted">Unknown</span>
              </td>
              <td>
                <router-link v-if="c.sessionId" class="small" :to="'/tracking/sessions/' + c.sessionId">Open journey</router-link>
              </td>
            </tr>
            <tr v-if="!cases.length && !loading">
              <td colspan="5">
                <div class="trk-empty"><strong>No OTP events</strong>Nothing recorded for these filters in this date range.</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const REASONS = {
  OTP_REQUEST_FAILED: 'Could not send the verification email',
  OTP_VERIFICATION_FAILED: 'Wrong or expired code',
  OTP_INVALID: 'Wrong or expired code',
  OTP_EXPIRED: 'Code expired',
  unknown: 'Reason not recorded'
};

const EVENT_LABELS = {
  OTP_REQUESTED: 'Code sent',
  OTP_FAILED: 'Failed',
  OTP_VERIFIED: 'Verified'
};

export default {
  props: {
    fromDate: { type: String, required: true },
    toDate: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      otpType: '',
      code: '',
      data: { total: 0, byType: {}, groups: [], cases: [] }
    };
  },
  computed: {
    types () {
      return [
        { value: '', label: 'All OTP' },
        { value: 'requested', label: 'Sent' },
        { value: 'failed', label: 'Failed' },
        { value: 'verified', label: 'Verified' }
      ];
    },
    visibleGroups () {
      return this.data.groups || [];
    },
    codeOptions () {
      return this.visibleGroups;
    },
    cases () {
      return this.data.cases || [];
    }
  },
  watch: {
    fromDate: 'load',
    toDate: 'load',
    code: 'load',
    otpType: 'load'
  },
  created () {
    this.load();
  },
  methods: {
    load () {
      this.loading = true;
      this.$onboardingTrackingService.getOtpProblems({
        from: this.fromDate,
        to: this.toDate,
        type: this.otpType,
        code: this.code
      }).then((res) => {
        this.data = (res && res.data) || { groups: [], cases: [], byType: {} };
      }).catch(() => {
        this.data = { total: 0, byType: {}, groups: [], cases: [] };
      }).finally(() => {
        this.loading = false;
      });
    },
    setType (type) {
      this.otpType = type;
      if (this.code && type !== 'failed' && type !== '') {
        this.code = '';
      }
    },
    typeCount (type) {
      const by = this.data.byType || {};
      if (!type) {
        return this.data.total || 0;
      }
      if (type === 'requested') return by.OTP_REQUESTED || 0;
      if (type === 'failed') return by.OTP_FAILED || 0;
      if (type === 'verified') return by.OTP_VERIFIED || 0;
      return 0;
    },
    reasonLabel (item) {
      const code = item.code || item.errorCode || 'unknown';
      if (REASONS[code]) {
        return REASONS[code];
      }
      return item.message || String(code).replace(/_/g, ' ');
    },
    eventLabel (name) {
      return EVENT_LABELS[name] || name;
    },
    eventPillClass (name) {
      if (name === 'OTP_FAILED') return 'bad';
      if (name === 'OTP_VERIFIED') return 'ok';
      return 'info';
    },
    when (value) {
      return value ? moment(value).local().format('DD/MM/YY HH:mm') : '-';
    },
    short (text) {
      return text && text.length > 120 ? text.slice(0, 120) + '…' : text;
    }
  }
};
</script>

<style scoped>
.pp-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.pp-kind {
  border: 1px solid #dfe7e3;
  background: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;
}
.pp-kind.active {
  background: #17333a;
  border-color: #17333a;
  color: #fff;
}
.pp-count {
  opacity: 0.7;
  margin-left: 0.2rem;
}
.pp-code {
  width: auto;
  max-width: 320px;
}
.pp-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.5rem;
}
.pp-group {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-align: left;
  border: 1px solid #dfe7e3;
  background: #fff;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  cursor: pointer;
}
.pp-group.active {
  border-color: #b42318;
  background: #fdecea;
}
.pp-group-count {
  font-size: 1.2rem;
  font-weight: 700;
  color: #b42318;
  min-width: 2ch;
}
.pp-group-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
